import { Suspense, useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";

// react-router components
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useMatch,
  useNavigate,
} from "react-router-dom";

// @mui material components
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";
import { ThemeProvider } from "@mui/material/styles";

//  React components
import MDBox from "components/MDBox";

//  React example components
import Configurator from "examples/Configurator";
import Sidenav from "examples/Sidenav";

//  React themes
import theme from "assets/theme";

import themePrimary from "assets/theme-primary";

import themeSecondary from "assets/theme-secondary";

//  React Dark Mode themes
import themeDark from "assets/theme-dark";

// RTL plugins

//  React routes
// import routes from "routes";

//  React contexts
import {
  setMiniSidenav,
  setOpenConfigurator,
  useMaterialUIController,
} from "context";

// Images
import { setGlobalState } from "Global";
import {
  BannerAPI,
  client,
  docstoreAPI,
  docstoreClient,
  analyticsClient,
} from "api";
import logo from "assets/images/sidenav-logo.png";

import CustomAlert from "utils/alert";
import Banner from "utils/banner";
import { ALL_PLATFORM_ROLES, defaultRoutes } from "routes";
import { useGlobalState } from "Global";
import { basicAPI } from "api";
import { createBrowserHistory } from "history";
import { replace } from "stylis";
import axios from "axios";
import OTPAuth from "layouts/authentication/2fa-auth";
import ChatBotPopup from "layouts/chat/ChatBotPopup";
import CustomLoader from "utils/customLoader";
import AccessDenied from "layouts/authentication/access-denied";
import CustomLogin from "layouts/authentication/microsoftlogin";
import ResetPassword from "layouts/authentication/reset-password";
import SignIn from "layouts/authentication/sign-in";
import SignUp from "layouts/authentication/sign-up";
import PingFederateCallback from "layouts/authentication/pingfederate-login";
import Logout from "layouts/logout";
import {
  PINGFED_PROMPT_NONE_KEY,
  passwordSignInNavigateArgs,
  savePingfedReturnTo,
} from "auth/authConfig";

const AUTH_ONLY_PATHS = [
  "/authentication/sign-up",
  "/authentication/reset-password",
  "/authentication/verify-email",
  "/auth2/callback",
  "/auth2/logout",
];

export default function App() {
  const auth = useAuth();
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const navigation = useNavigate();
  const [user, setUser] = useState({});
  const history = createBrowserHistory();
  const [bannerState, setBannerState] = useState({});

  const [routes, setRoutes] = useState(null);
  const [errorState] = useGlobalState("error");
  const [userDetails] = useGlobalState("userDetails");
  const [apiError, setApiError] = useState(false);
  const [currentTheme, setCurrentTheme] = useState("Primary");
  const sessionBootstrapInFlightRef = useRef(false);
  const handledAuthErrorRef = useRef(null);

  client.interceptors.request.use(
    async (config) => {
      if (!config.headers["Authorization"]) {
        const accessToken = userDetails?.access_token || userDetails?.token;
        if (accessToken) {
          config.headers["Authorization"] = `Bearer ${accessToken}`;
        }
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  client.interceptors.response.use(
    function (response) {
      return response;
    },
    async (error) => {
      let updatedRequest = await globalErrorHandler(error);
      if (updatedRequest) {
        return updatedRequest;
      }
      return Promise.reject(error);
    }
  );

  const globalErrorHandler = async (error) => {
    const prevRequest = error?.config;
    if (error.response) {
      if (
        [401].includes(error.response?.status) &&
        prevRequest.url != "/me" &&
        !auth.isAuthenticated &&
        window.location.pathname !== "/auth2/callback"
      ) {
        // Only one refresh token request should be sent
        if (userDetails?.access_token && !prevRequest.sent) {
          setGlobalState("userDetails", () => {});
          prevRequest.sent = true;
          let newAccessToken = await basicAPI
            .refreshToken()
            .then((res) => {
              if (res.status == 200) {
                const tempUser = res.data;
                setGlobalState("userDetails", (prevGlobalState) => res.data);
                return res.data["access_token"];
              }
            })
            .catch((error) => {
              // Incase of refresh token failure, logout the user and redirect to login page
              if (window.location.pathname != "/authentication/sign-in") {
                basicAPI.logout().then((res) => {
                  if (res.status == 200) {
                    navigation("/authentication/sign-in", {
                      state: { from: history.location, replace: true },
                    });
                    setGlobalState("userDetails", null);
                    setGlobalState("error", {
                      open: true,
                      message: "Login Expired! Please Login Again",
                      type: "error",
                      code: error.response.status,
                    });
                  }
                });
              }
              return null;
            });
          // If new access token is received, update the request header and resend the request
          if (newAccessToken) {
            prevRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
            client.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
            return client(prevRequest);
          }
        }
      } else if ([422].includes(error.response?.status)) {
        setGlobalState("error", {
          open: true,
          message: "Invalid format. Input value or format is Invalid.",
          type: "error",
          code: error.response.status,
        });
      } else if (
        ![422, 455, 456, 304, 405, 403].includes(error.response?.status)
      ) {
        if (typeof error.response?.data == typeof "") {
          setGlobalState("error", {
            open: true,
            message: error.response?.data,
            type: "error",
            code: error.response.status,
          });
        }
      }
    }
  };

  const unlisten = history.listen(({ location, action }) => {
    console.log(action, location.pathname);
  });

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () =>
    setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getBannerState = () => {
    BannerAPI.getBannerStatus()
      .then((res) => {
        if (res.status == 200) {
          setBannerState([]);
        }
      })
      .catch((error) => {
        if ([455].includes(error.response?.status)) {
          let bannerList = error.response.data;
          let tempBannerState = {};
          bannerList.map((banner) => {
            let routeObject = defaultRoutes.find(
              (obj) => obj.name == banner.name
            );
            if (routeObject) {
              tempBannerState[routeObject.key] = banner;
            }
          });
          setBannerState(tempBannerState);
        }
      });
  };

  // PingFed/OIDC failure (incl. silent renew): clear OIDC state and fall back
  // to username/password sign-in. Callback route handles its own errors.
  useEffect(() => {
    if (!auth.error) {
      handledAuthErrorRef.current = null;
      return;
    }
    if (auth.isLoading) return;
    if (pathname === "/auth2/callback") return;

    const errorKey = auth.error.message || String(auth.error);
    if (handledAuthErrorRef.current === errorKey) return;
    handledAuthErrorRef.current = errorKey;

    const hadSession = Boolean(userDetails?.permission);

    void auth.removeUser?.().finally(() => {
      setGlobalState("userDetails", null);
      if (pathname !== "/authentication/sign-in") {
        if (hadSession) {
          setGlobalState("error", {
            open: true,
            message:
              "PingFederate session expired. Please log in with your username and password.",
            type: "error",
          });
        }
        navigation("/authentication/sign-in", passwordSignInNavigateArgs());
      } else {
        sessionStorage.removeItem(PINGFED_PROMPT_NONE_KEY);
      }
    });
  }, [
    auth.error,
    auth.isLoading,
    auth.removeUser,
    navigation,
    pathname,
    userDetails?.permission,
  ]);

  useEffect(() => {
    if (auth.isLoading || auth.activeNavigator) return;

    if (AUTH_ONLY_PATHS.includes(pathname) || userDetails?.permission) {
      return;
    }

    if (auth.error) {
      // Handled by the auth.error effect above
      return;
    }

    if (sessionBootstrapInFlightRef.current) return;
    sessionBootstrapInFlightRef.current = true;

    const applyBearer = (token) => {
      if (!token) return;
      client.defaults.headers.common.Authorization = `Bearer ${token}`;
      docstoreClient.defaults.headers.common.Authorization = `Bearer ${token}`;
      analyticsClient.defaults.headers.common.Authorization = `Bearer ${token}`;
    };

    const applySession = (data, oidcToken) => {
      const accessToken = oidcToken || data?.access_token || data?.token;
      applyBearer(accessToken);
      setGlobalState("userDetails", {
        ...data,
        ...(accessToken ? { access_token: accessToken } : {}),
      });
      if (pathname === "/authentication/sign-in") {
        navigation("/", { replace: true });
      }
    };

    const redirectToSignIn = () => {
      if (pathname !== "/authentication/sign-in") {
        navigation("/authentication/sign-in", {
          state: { from: history.location, replace: true },
        });
      }
    };

    const loadProfileWithToken = (oidcToken) => {
      applyBearer(oidcToken);
      return basicAPI.refreshToken().then((res) => {
        if (res.status == 200) {
          applySession(res.data, oidcToken);
        }
      });
    };

    const bootstrap = async () => {
      try {
        // Existing OIDC session in this tab (e.g. after token renew)
        if (auth.isAuthenticated && auth.user?.access_token) {
          await loadProfileWithToken(auth.user.access_token);
          return;
        }

        // Username/password cookie session
        try {
          const res = await basicAPI.refreshToken();
          if (res.status == 200) {
            applySession(res.data);
            return;
          }
        } catch {
          // No cookie session
        }

        // Already on sign-in: do not bounce through PingFed for password-only visitors
        if (pathname === "/authentication/sign-in") {
          return;
        }

        // New tab / cold load: top-level prompt=none uses PingFed SSO cookie (first-party)
        savePingfedReturnTo(`${window.location.pathname}${window.location.search}`);
        sessionStorage.setItem(PINGFED_PROMPT_NONE_KEY, "1");
        await auth.signinRedirect({ prompt: "none" });
      } catch {
        sessionStorage.removeItem(PINGFED_PROMPT_NONE_KEY);
        try {
          await auth.removeUser?.();
        } catch {
          // ignore
        }
        redirectToSignIn();
      } finally {
        sessionBootstrapInFlightRef.current = false;
      }
    };

    void bootstrap();
  }, [
    auth.activeNavigator,
    auth.error,
    auth.isAuthenticated,
    auth.isLoading,
    auth.removeUser,
    auth.signinRedirect,
    auth.user,
    navigation,
    pathname,
    userDetails?.permission,
  ]);

  // Keep the OIDC access token in sync after silent renew, but never create a
  // token-only userDetails blob. That used to race ahead of /me, register the
  // catch-all with no permission, and bounce hard reloads to /access-denied.
  useEffect(() => {
    const oidcToken = auth.user?.access_token;
    const isOidcAuthRoute = pathname === "/auth2/callback";

    if (!auth.isAuthenticated || !oidcToken || isOidcAuthRoute) return;
    if (!userDetails?.permission) return;
    if (userDetails?.access_token === oidcToken) return;

    client.defaults.headers.common.Authorization = `Bearer ${oidcToken}`;
    docstoreClient.defaults.headers.common.Authorization = `Bearer ${oidcToken}`;
    analyticsClient.defaults.headers.common.Authorization = `Bearer ${oidcToken}`;

    setGlobalState("userDetails", (prevState) => ({
      ...(prevState || {}),
      access_token: oidcToken,
    }));
  }, [
    auth.isAuthenticated,
    auth.user,
    pathname,
    userDetails?.access_token,
    userDetails?.permission,
  ]);

  useEffect(() => {
    // } else {
    //   client.defaults.headers.common.Authorization = `Bearer ${userDetails["access_token"]}`;
    //   setUser(userDetails);
    //   const temproutes = defaultRoutes.filter((route) => {
    //     if (route && route.access.includes(userDetails.permission)) {
    //       return route;
    //     }
    //   });

    //   setRoutes(temproutes);
    //   if (window.location.pathname == "/authentication/sign-in") {
    //     navigation("/");
    //   }
    //   // navigation("/dashboard")
    // }

    if (Object.keys(bannerState).length > 0) {
      const pattern = new RegExp(Object.keys(bannerState).join("|"), "g");

      let matchedString = window.location.pathname.match(pattern);

      if (matchedString) {
        setGlobalState("banner", {
          open: true,
          message: bannerState[matchedString[0]].message,
          type: bannerState[matchedString[0]].message_type,
        });
      }
    }
  }, [window.location.pathname]);

  useEffect(() => {
    const accessToken = userDetails?.access_token || userDetails?.token;
    // Wait for a real profile (permission) before mounting app routes / catch-alls.
    if (!accessToken || !userDetails?.permission) {
      return;
    }

    client.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    docstoreClient.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    analyticsClient.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    setUser({ ...userDetails, access_token: accessToken });

    const currentEmail = (userDetails?.email || "").trim().toLowerCase();
    const temproutes = defaultRoutes.filter((route) => {
      if (!route || !route.access.includes(userDetails.permission)) {
        return false;
      }
      // Some screens, such as the support desk, belong to specific mailboxes.
      if (route.allowedEmails?.length) {
        return route.allowedEmails
          .map((email) => email.toLowerCase())
          .includes(currentEmail);
      }
      return true;
    });
    setRoutes(temproutes);
    getBannerState();
  }, [userDetails]);

  // If a prior race left the user on /access-denied, recover once the profile loads.
  useEffect(() => {
    if (pathname !== "/access-denied") return;
    if (!user?.permission || !user?.id) return;
    if (!ALL_PLATFORM_ROLES.includes(user.permission)) return;

    navigation(`/${user.id}/cases`, { replace: true });
  }, [navigation, pathname, user?.id, user?.permission]);

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="medium" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  return (
    <>
      <ThemeProvider
        theme={currentTheme != "Primary" ? themePrimary : themeSecondary}
      >
        <CssBaseline />
        {layout === "dashboard" && (
          <>
            {/* {console.log(routes)} */}
            {routes?.length > 0 && (
              <Sidenav
                color={sidenavColor}
                brand={logo}
                brandName="NeatProcess"
                routes={routes}
                user={user}
                setCurrentTheme={setCurrentTheme}
                // onMouseEnter={handleOnMouseEnter}
                // onMouseLeave={handleOnMouseLeave}
              />
            )}
          </>
        )}
        <CustomAlert />
        <Banner />
        {userDetails?.access_token ? <ChatBotPopup routes={defaultRoutes} /> : null}
        <Suspense
          fallback={
            <div
              style={{
                width: "100vw",
                height: "100vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CustomLoader />
            </div>
          }
        >
          <Routes>
          <Route path="/authentication/sign-in" element={<SignIn />} />
          <Route path="/authentication/sign-up" element={<SignUp />} />
          <Route
            path="/authentication/reset-password"
            element={<ResetPassword />}
          />
          <Route path="/authentication/verify-email" element={<OTPAuth />} />
          <Route path="/access-denied" element={<AccessDenied />} />
          <Route path="/login-callback" element={<CustomLogin />} />
          <Route path="/auth2/callback" element={<PingFederateCallback />} />
          <Route path="/auth2/logout" element={<Logout />} />

          {routes?.length > 0 &&
            routes.map((route) => {
              if (
                route &&
                route.path &&
                route.access?.includes(user.permission)
              ) {
                return (
                  <>
                    {
                      <Route
                        path={route.path}
                        element={route.component}
                        key={route.key}
                      />
                    }
                  </>
                );
              }
            })}

          {routes?.length > 0 && (
            <Route
              path="/"
              element={
                <Navigate
                  to={
                    ALL_PLATFORM_ROLES.includes(user.permission)
                      ? `/${user.id}/cases`
                      : "/access-denied"
                  }
                />
              }
            />
          )}
          {user?.access_token && user?.permission && (
            <Route
              path="*"
              element={
                <Navigate
                  to={
                    ALL_PLATFORM_ROLES.includes(user.permission)
                      ? `/${user.id}/cases`
                      : "/access-denied"
                  }
                />
              }
            />
          )}
          {/* {user?.access_token === undefined && (
            <Route
              path="*"
              element={
                <Navigate
                  to={"/authentication/sign-in"}
                />
              }
            />
          )} */}
          </Routes>
        </Suspense>
      </ThemeProvider>
    </>
  );
}
